/**
 * Egyszerű energia-alapú csend-detektálás MediaStream-hez (turn-based hang).
 */

/**
 * @param {{
 *   stream: MediaStream,
 *   onSilence: () => void,
 *   silenceMs?: number,
 *   minSpeechMs?: number,
 *   maxMs?: number,
 *   threshold?: number,
 * }} options
 * @returns {{ stop: () => void }}
 */
export function createSilenceDetector(options) {
    const {
        stream,
        onSilence,
        silenceMs = 1400,
        minSpeechMs = 700,
        maxMs = 40000,
        threshold = 0.018,
    } = options;

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (! AudioCtx || ! stream) {
        return { stop() {} };
    }

    const ctx = new AudioCtx();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);

    const data = new Uint8Array(analyser.fftSize);
    let stopped = false;
    let speechStartedAt = null;
    let silenceStartedAt = null;
    const startedAt = Date.now();
    let raf = 0;

    const stop = () => {
        if (stopped) {
            return;
        }
        stopped = true;
        if (raf) {
            cancelAnimationFrame(raf);
            raf = 0;
        }
        try {
            source.disconnect();
        } catch {
            // ignore
        }
        try {
            ctx.close();
        } catch {
            // ignore
        }
    };

    const tick = () => {
        if (stopped) {
            return;
        }

        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i += 1) {
            const v = (data[i] - 128) / 128;
            sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        const now = Date.now();

        if (now - startedAt >= maxMs) {
            stop();
            onSilence();

            return;
        }

        if (rms >= threshold) {
            if (speechStartedAt === null) {
                speechStartedAt = now;
            }
            silenceStartedAt = null;
        } else if (speechStartedAt !== null && now - speechStartedAt >= minSpeechMs) {
            if (silenceStartedAt === null) {
                silenceStartedAt = now;
            } else if (now - silenceStartedAt >= silenceMs) {
                stop();
                onSilence();

                return;
            }
        }

        raf = requestAnimationFrame(tick);
    };

    if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
    }

    raf = requestAnimationFrame(tick);

    return { stop };
}
